import {
  PublicVoiceCaptureResponseSchema,
  PublicVoiceCaptureUploadResponseSchema,
  type PublicVoiceCaptureResponse,
  type VoiceMobileRecordingMime,
} from '@cloneai/shared-types';
import { CheckCircle2, Mic, RotateCcw, Send, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

import { env } from './env.js';
import { VoiceCaptureAudioPreview } from './VoiceCaptureAudioPreview.js';
import {
  checkRecording,
  chooseRecordingMime,
  normalizeRecordingMime,
} from './voiceCaptureRecording.js';

interface VoiceCaptureAppProps {
  readonly captureId: string;
  readonly token: string | null;
}

interface LocalRecording {
  readonly blob: Blob;
  readonly url: string;
  readonly mime: VoiceMobileRecordingMime;
  readonly durationMs: number;
  readonly errors: readonly string[];
  readonly warnings: readonly string[];
}

type RecordingKind = 'consent' | 'sample';

async function problemMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown; title?: unknown };
    if (typeof body.detail === 'string') return body.detail;
    if (typeof body.title === 'string') return body.title;
  } catch {
    // Fall through to the stable HTTP message.
  }
  return `Request failed (HTTP ${response.status}).`;
}

export function VoiceCaptureApp({ captureId, token }: VoiceCaptureAppProps): JSX.Element {
  const apiBase = env.VITE_API_BASE_URL.replace(/\/$/, '');
  const [capture, setCapture] = useState<PublicVoiceCaptureResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [recordings, setRecordings] = useState<Partial<Record<RecordingKind, LocalRecording>>>({});
  const [recordingKind, setRecordingKind] = useState<RecordingKind | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const contextRef = useRef<AudioContext | null>(null);
  const levelTimerRef = useRef<number | null>(null);
  const stopTimerRef = useRef<number | null>(null);
  const startedAtRef = useRef(0);
  const chunksRef = useRef<BlobPart[]>([]);
  const levelRef = useRef({ sum: 0, count: 0, peak: 0 });
  const recordingUrlsRef = useRef(new Set<string>());

  const request = useCallback(
    async (path: string, init: RequestInit = {}): Promise<Response> => {
      if (!token) throw new Error('This recording link is incomplete. Ask for a new link.');
      return fetch(`${apiBase}${path}`, {
        ...init,
        headers: {
          'x-cloneai-voice-capture-token': token,
          ...(init.body ? { 'content-type': 'application/json' } : {}),
          ...init.headers,
        },
      });
    },
    [apiBase, token],
  );

  const loadCapture = useCallback(async (): Promise<void> => {
    try {
      let response: Response | null = null;
      // GSI-backed lookup is eventually consistent immediately after link
      // creation/replacement. A short bounded retry avoids a false 404 when
      // the recipient opens a just-created link right away.
      for (let attempt = 0; attempt < 5; attempt += 1) {
        response = await request(`/v1/public/voice-captures/${encodeURIComponent(captureId)}`);
        if (response.status !== 404 || attempt === 4) break;
        await new Promise((resolve) => window.setTimeout(resolve, 250 * (attempt + 1)));
      }
      if (!response) throw new Error('Could not open this recording link.');
      if (!response.ok) throw new Error(await problemMessage(response));
      setCapture(PublicVoiceCaptureResponseSchema.parse(await response.json()));
      setLoadError(null);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not open this recording link.');
    }
  }, [captureId, request]);

  useEffect(() => {
    void loadCapture();
  }, [loadCapture]);

  useEffect(() => {
    if (capture?.status !== 'processing') return;
    const timer = window.setInterval(() => void loadCapture(), 3_000);
    return () => window.clearInterval(timer);
  }, [capture?.status, loadCapture]);

  const cleanupRecorder = useCallback((): void => {
    if (levelTimerRef.current !== null) window.clearInterval(levelTimerRef.current);
    if (stopTimerRef.current !== null) window.clearTimeout(stopTimerRef.current);
    levelTimerRef.current = null;
    stopTimerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    void contextRef.current?.close();
    contextRef.current = null;
    recorderRef.current = null;
  }, []);

  useEffect(
    () => () => {
      cleanupRecorder();
      recordingUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    },
    [cleanupRecorder],
  );

  const stopRecording = useCallback((): void => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  }, []);

  const startRecording = async (kind: RecordingKind): Promise<void> => {
    setSubmitError(null);
    const mime = chooseRecordingMime();
    if (!mime || !navigator.mediaDevices?.getUserMedia) {
      setSubmitError(
        'This browser cannot make a supported audio recording. Try current Safari or Chrome.',
      );
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      const context = new AudioContext();
      const analyser = context.createAnalyser();
      analyser.fftSize = 2_048;
      context.createMediaStreamSource(stream).connect(analyser);
      const samples = new Uint8Array(analyser.fftSize);
      chunksRef.current = [];
      levelRef.current = { sum: 0, count: 0, peak: 0 };
      streamRef.current = stream;
      contextRef.current = context;
      recorderRef.current = recorder;
      startedAtRef.current = performance.now();
      setRecordingKind(kind);
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        const durationMs = Math.min(
          30_000,
          Math.max(1, Math.round(performance.now() - startedAtRef.current)),
        );
        const normalized = normalizeRecordingMime(recorder.mimeType || mime);
        const blob = new Blob(chunksRef.current, { type: normalized ?? mime });
        const levels = levelRef.current;
        const checks = checkRecording({
          kind,
          durationMs,
          bytes: blob.size,
          averageRms: levels.count === 0 ? 0 : levels.sum / levels.count,
          peak: levels.peak,
        });
        const errors = normalized
          ? checks.errors
          : [...checks.errors, 'The browser produced an unsupported audio format.'];
        const url = URL.createObjectURL(blob);
        recordingUrlsRef.current.add(url);
        const next: LocalRecording = {
          blob,
          url,
          mime: normalized ?? mime,
          durationMs,
          errors,
          warnings: checks.warnings,
        };
        setRecordings((current) => {
          const previous = current[kind];
          if (previous) {
            URL.revokeObjectURL(previous.url);
            recordingUrlsRef.current.delete(previous.url);
          }
          return { ...current, [kind]: next };
        });
        setRecordingKind(null);
        cleanupRecorder();
      };
      levelTimerRef.current = window.setInterval(() => {
        analyser.getByteTimeDomainData(samples);
        let squareSum = 0;
        let peak = 0;
        for (const sample of samples) {
          const amplitude = Math.abs((sample - 128) / 128);
          squareSum += amplitude * amplitude;
          peak = Math.max(peak, amplitude);
        }
        const rms = Math.sqrt(squareSum / samples.length);
        levelRef.current.sum += rms;
        levelRef.current.count += 1;
        levelRef.current.peak = Math.max(levelRef.current.peak, peak);
      }, 100);
      stopTimerRef.current = window.setTimeout(stopRecording, 29_500);
      recorder.start(250);
    } catch (error) {
      cleanupRecorder();
      setRecordingKind(null);
      setSubmitError(
        error instanceof DOMException && error.name === 'NotAllowedError'
          ? 'Microphone access was denied. Allow microphone access and try again.'
          : 'The microphone could not be started.',
      );
    }
  };

  const uploadRecording = async (kind: RecordingKind, recording: LocalRecording): Promise<void> => {
    const create = await request(
      `/v1/public/voice-captures/${encodeURIComponent(captureId)}/uploads`,
      {
        method: 'POST',
        body: JSON.stringify({
          kind,
          mime: recording.mime,
          bytes: recording.blob.size,
          durationMs: recording.durationMs,
        }),
      },
    );
    if (!create.ok) throw new Error(await problemMessage(create));
    const upload = PublicVoiceCaptureUploadResponseSchema.parse(await create.json());
    const put = await fetch(upload.uploadUrl, {
      method: 'PUT',
      headers: { 'content-type': recording.mime },
      body: recording.blob,
    });
    if (!put.ok) throw new Error(`Audio upload failed (HTTP ${put.status}).`);
  };

  const submit = async (): Promise<void> => {
    const consent = recordings.consent;
    const sample = recordings.sample;
    if (!consent || !sample || consent.errors.length > 0 || sample.errors.length > 0) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await uploadRecording('consent', consent);
      await uploadRecording('sample', sample);
      const response = await request(
        `/v1/public/voice-captures/${encodeURIComponent(captureId)}/complete`,
        { method: 'POST' },
      );
      if (!response.ok) throw new Error(await problemMessage(response));
      await loadCapture();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Could not submit the recordings.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <main className="voice-capture-page">
        <section className="voice-capture-card">
          <h1>Recording unavailable</h1>
          <p>{loadError}</p>
        </section>
      </main>
    );
  }
  if (!capture) {
    return (
      <main className="voice-capture-page">
        <section className="voice-capture-card">
          <p>Opening secure recording…</p>
        </section>
      </main>
    );
  }
  if (capture.status === 'processing') {
    return (
      <main className="voice-capture-page">
        <section className="voice-capture-card voice-capture-result">
          <ShieldCheck size={44} />
          <h1>Recordings received</h1>
          <p>
            The onboarding submission is being validated. OpenAI will be contacted only after both
            recordings pass. You can close this page.
          </p>
        </section>
      </main>
    );
  }
  if (capture.status === 'ready') {
    return (
      <main className="voice-capture-page">
        <section className="voice-capture-card voice-capture-result">
          <CheckCircle2 size={44} />
          <h1>Voice setup is ready</h1>
          <p>The account owner can now review and activate the voice. You can close this page.</p>
        </section>
      </main>
    );
  }
  if (capture.status === 'failed') {
    return (
      <main className="voice-capture-page">
        <section className="voice-capture-card">
          <h1>Voice setup failed</h1>
          <p>{capture.failureMessage ?? 'Ask the account owner for a new recording link.'}</p>
        </section>
      </main>
    );
  }

  const renderRecorder = (
    kind: RecordingKind,
    title: string,
    instructions: JSX.Element,
  ): JSX.Element => {
    const recording = recordings[kind];
    const active = recordingKind === kind;
    return (
      <section className="voice-capture-step">
        <div className="voice-capture-step-number">{kind === 'consent' ? '1' : '2'}</div>
        <div className="voice-capture-step-body">
          <h2>{title}</h2>
          {instructions}
          {recording ? (
            <VoiceCaptureAudioPreview label={`${title} playback`} src={recording.url} />
          ) : null}
          {recording?.errors.map((message) => (
            <p className="voice-capture-error" key={message}>
              {message}
            </p>
          ))}
          {recording?.warnings.map((message) => (
            <p className="voice-capture-warning" key={message}>
              {message}
            </p>
          ))}
          <button
            className={active ? 'voice-capture-button recording' : 'voice-capture-button'}
            type="button"
            disabled={recordingKind !== null && !active}
            onClick={() => (active ? stopRecording() : void startRecording(kind))}
          >
            {active ? (
              <>
                <span className="voice-capture-pulse" /> Stop recording
              </>
            ) : recording ? (
              <>
                <RotateCcw size={18} /> Record again
              </>
            ) : (
              <>
                <Mic size={18} /> Start recording
              </>
            )}
          </button>
        </div>
      </section>
    );
  };

  const canSubmit = Boolean(
    recordings.consent &&
    recordings.sample &&
    recordings.consent.errors.length === 0 &&
    recordings.sample.errors.length === 0,
  );

  return (
    <main className="voice-capture-page">
      <section className="voice-capture-card">
        <div className="voice-capture-eyebrow">Secure voice setup</div>
        <h1>Record voice for {capture.agentName}</h1>
        <p>
          Use the same speaker for both recordings. Find a quiet room, hold the phone 15–25 cm away,
          and speak naturally.
        </p>
        {renderRecorder(
          'consent',
          'Record consent',
          <blockquote>{capture.consentPhrase}</blockquote>,
        )}
        {renderRecorder(
          'sample',
          'Record a voice sample',
          <p>
            Speak naturally for 10–20 seconds. Introduce yourself and describe something you know
            well. Do not read the consent phrase again.
          </p>,
        )}
        {submitError ? <p className="voice-capture-error">{submitError}</p> : null}
        <button
          className="voice-capture-submit"
          type="button"
          disabled={!canSubmit || submitting || recordingKind !== null}
          onClick={() => void submit()}
        >
          <Send size={18} /> {submitting ? 'Uploading securely…' : 'Submit both recordings'}
        </button>
        <p className="voice-capture-privacy">
          <ShieldCheck size={16} /> The link expires {new Date(capture.expiresAt).toLocaleString()}.
          Recordings are private and used only to create this agent’s voice.
        </p>
      </section>
    </main>
  );
}
